import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../app/Store";
import { LoadingState } from "../../app/LoadingState";
import {
  beginRequest,
  completeRequest,
  rejectRequest,
  RequestState,
} from "../../app/store/RequestState";
import { getAlertsHistory, getIssuesHistory } from "../API";
import { FaultHistoryRecords, mergeHistory } from "./historyWindows";

export type FaultHistoryKind = "alerts" | "issues" | "all";
export interface FaultHistoryRequest {
  siteId: string;
  kind: FaultHistoryKind;
  refreshKey: number;
  older: boolean;
}
export interface FaultHistoryEntry extends FaultHistoryRecords, RequestState {
  oldestFrom?: number;
  fetchedAt?: number;
  refreshKey?: number;
  lastRequestOlder?: boolean;
}
interface FaultHistoryState {
  entries: Record<string, FaultHistoryEntry>;
  consumers: Record<string, number>;
}
const initialState: FaultHistoryState = { entries: {}, consumers: {} };
const historyWindowMilliseconds = 180 * 24 * 60 * 60 * 1000;
export const getFaultHistoryKey = (siteId: string, kind: FaultHistoryKind) =>
  JSON.stringify([siteId, kind]);

function pruneHistoryEntries(state: FaultHistoryState) {
  const keys = Object.keys(state.entries);
  let excess = keys.length - 50;
  for (const key of keys) {
    if (excess <= 0) {
      break;
    }
    // Mounted consumers own their snapshots and pending responses, even above the cache budget.
    if (!state.consumers[key]) {
      delete state.entries[key];
      excess -= 1;
    }
  }
}

export const fetchFaultHistory = createAsyncThunk(
  "faultHistory/fetch",
  async (args: FaultHistoryRequest, { getState, signal: parentSignal }) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    parentSignal.addEventListener("abort", abort, { once: true });
    if (parentSignal.aborted) {
      abort();
    }
    const signal = controller.signal;
    try {
      const key = getFaultHistoryKey(args.siteId, args.kind);
      const cached = (getState() as RootState).faultHistory.entries[key];
      const to = args.older ? new Date(cached.oldestFrom!) : new Date();
      const oldestFrom =
        args.older || cached.oldestFrom === undefined
          ? Math.max(0, to.getTime() - historyWindowMilliseconds)
          : cached.oldestFrom;
      let records: FaultHistoryRecords = args.older
        ? { alerts: cached.alerts, issues: cached.issues }
        : { alerts: [], issues: [] };
      let windowTo = to;
      while (windowTo.getTime() > oldestFrom) {
        const from = new Date(Math.max(oldestFrom, windowTo.getTime() - historyWindowMilliseconds));
        const [alerts, issues] = await Promise.all([
          args.kind === "issues"
            ? Promise.resolve({ singleDeviceAlerts: [], aggregateAlerts: [] })
            : getAlertsHistory(args.siteId, from, windowTo, signal),
          args.kind === "alerts"
            ? Promise.resolve([])
            : getIssuesHistory(args.siteId, from, windowTo, signal),
        ]);
        if (signal.aborted) {
          throw new DOMException("History read cancelled", "AbortError");
        }
        records = mergeHistory(records, {
          alerts: [
            ...alerts.singleDeviceAlerts.map((alert) => ({
              ...alert,
              kind: "single-device" as const,
              history: true as const,
            })),
            ...alerts.aggregateAlerts.map((alert) => ({
              ...alert,
              kind: "aggregated" as const,
              history: true as const,
            })),
          ],
          issues,
        });
        windowTo = from;
      }
      return {
        ...records,
        oldestFrom,
        fetchedAt: args.older ? cached.fetchedAt : Date.now(),
        refreshKey: args.refreshKey,
      };
    } catch (error) {
      controller.abort();
      throw error;
    } finally {
      parentSignal.removeEventListener("abort", abort);
    }
  }
);

const faultHistorySlice = createSlice({
  name: "faultHistory",
  initialState,
  reducers: {
    faultHistoryRetained(state, action: PayloadAction<string>) {
      state.consumers[action.payload] = (state.consumers[action.payload] ?? 0) + 1;
    },
    faultHistoryReleased(state, action: PayloadAction<string>) {
      const remaining = (state.consumers[action.payload] ?? 1) - 1;
      if (remaining > 0) {
        state.consumers[action.payload] = remaining;
      } else {
        delete state.consumers[action.payload];
      }
      pruneHistoryEntries(state);
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchFaultHistory.pending, (state, action) => {
        const key = getFaultHistoryKey(action.meta.arg.siteId, action.meta.arg.kind);
        const current = (state.entries[key] ??= {
          alerts: [],
          issues: [],
          loadingState: LoadingState.None,
        });
        beginRequest(current, action.meta.requestId);
        current.lastRequestOlder = action.meta.arg.older;
        if (!action.meta.arg.older) {
          current.fetchedAt = undefined;
        }
        pruneHistoryEntries(state);
      })
      .addCase(fetchFaultHistory.fulfilled, (state, action) => {
        const key = getFaultHistoryKey(action.meta.arg.siteId, action.meta.arg.kind);
        const current = state.entries[key];
        if (!current || !completeRequest(current, action.meta.requestId)) {
          return;
        }
        Object.assign(current, action.payload);
        pruneHistoryEntries(state);
      })
      .addCase(fetchFaultHistory.rejected, (state, action) => {
        const current = state.entries[getFaultHistoryKey(action.meta.arg.siteId, action.meta.arg.kind)];
        if (current) {
          rejectRequest(current, action);
          pruneHistoryEntries(state);
        }
      }),
});
export const { faultHistoryRetained, faultHistoryReleased } = faultHistorySlice.actions;
export const faultHistoryReducer = faultHistorySlice.reducer;
